import { notifications } from '@mantine/notifications'
import type { RefObject } from 'react'
import { useCallback, useEffect, useRef } from 'react'

import { advanceQueue } from '~/components/player/advance'
import {
  canSwapGaplessly,
  freshBaseline,
  hasBufferHoleAhead,
  MAX_RECOVERY_ATTEMPTS,
  observeStall,
  passedScrobbleMark,
  planAutoResume,
  RECOVERY_TIMEOUT_MS,
  STALL_TICK_MS,
  type StallBaseline,
} from '~/components/player/audio-recovery'
import type { AudioChannel, PlayerState, QueueItem } from '~/components/player/types'
import { updateMediaSession } from '~/components/player/utils'
import { useAppStore } from '~/hooks/use-app-store'
import { streamUrl } from '~/lib/cover-art'
import { describeMediaError } from '~/lib/media-error'
import { isIOS } from '~/lib/platform'
import { replayGainToLinear } from '~/lib/replay-gain'
import { safePlay } from '~/lib/safe-play'

/** Smoothly transition a GainNode to a target value (avoids clicks/pops from instant changes) */
function smoothGain(gainNode: GainNode, value: number, rampTime = 0.02) {
  const now = gainNode.context.currentTime
  gainNode.gain.cancelScheduledValues(now)
  gainNode.gain.setValueAtTime(gainNode.gain.value, now)
  gainNode.gain.linearRampToValueAtTime(value, now + rampTime)
}

interface UseAudioElementOptions {
  stateRef: RefObject<PlayerState>
  currentTrackIdRef: RefObject<number | null>
  scrobbledRef: RefObject<boolean>
  currentTimeRef: RefObject<number>
  pendingRadioAdvanceRef: RefObject<boolean>
  intentionalPauseRef: RefObject<boolean>
  notifyTimeSubscribers: () => void
  setState: (updater: PlayerState | ((prev: PlayerState) => PlayerState)) => void
  volume: number
  muted: boolean
  replayGainEnabled: boolean
  replayGainPreampDb: number
}

export function useAudioElement({
  stateRef,
  currentTrackIdRef,
  scrobbledRef,
  currentTimeRef,
  pendingRadioAdvanceRef,
  intentionalPauseRef,
  notifyTimeSubscribers,
  setState,
  volume,
  muted,
  replayGainEnabled,
  replayGainPreampDb,
}: UseAudioElementOptions) {
  const channelARef = useRef<AudioChannel | null>(null)
  const channelBRef = useRef<AudioChannel | null>(null)
  const activeChannelRef = useRef<'A' | 'B'>('A')
  const audioContextRef = useRef<AudioContext | null>(null)
  const crossfadeActiveRef = useRef(false)
  const crossfadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  /** Apply ReplayGain to a channel for a given track. Reads settings off
   * stateRef so the identity stays stable — every effect below depends on it. */
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  const applyReplayGain = useCallback((channel: AudioChannel, track: QueueItem | null) => {
    if (!channel.gainNode) return
    if (!stateRef.current.replayGainEnabled) {
      smoothGain(channel.gainNode, 1.0)
      return
    }
    const rg = track?.replayGain
    const gain = replayGainToLinear(
      rg?.trackGain,
      rg?.trackPeak,
      stateRef.current.replayGainPreampDb
    )
    smoothGain(channel.gainNode, gain)
  }, [])

  /** Initialize or resume AudioContext (must be called from user gesture).
   * No-op on iOS: routing the element through an AudioContext stops background
   * playback (the context is suspended when the PWA is backgrounded), so iOS
   * plays the bare <audio> element instead.
   * Refs only, so the identity stays stable across renders. */
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  const ensureAudioContext = useCallback((): AudioContext | null => {
    if (isIOS()) return null
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume().catch((err: unknown) => {
          console.error('[Player] AudioContext resume failed:', err)
        })
      }
      return audioContextRef.current
    }
    const ctx = new AudioContext({ latencyHint: 'playback' })
    audioContextRef.current = ctx

    for (const ch of [channelARef.current, channelBRef.current]) {
      if (!ch) continue
      // Re-wire nodes when context changes (old nodes from a closed context are orphaned)
      if (ch.source && ch.source.context !== ctx) {
        ch.source = null
        ch.gainNode = null
        ch.analyserNode = null
      }
      if (!ch.source) {
        ch.source = ctx.createMediaElementSource(ch.audio)
        ch.gainNode = ctx.createGain()
        ch.analyserNode = ctx.createAnalyser()
        ch.analyserNode.fftSize = 2048
        ch.source.connect(ch.gainNode)
        ch.gainNode.connect(ctx.destination)
        ch.gainNode.connect(ch.analyserNode)
      }
    }

    // Fresh gain nodes start at unity. Callers that already loaded a track before
    // the context existed would otherwise be stuck ungained for the whole track.
    const activeChannel =
      activeChannelRef.current === 'A' ? channelARef.current : channelBRef.current
    if (activeChannel) {
      const s = stateRef.current
      applyReplayGain(activeChannel, s.currentIndex >= 0 ? (s.queue[s.currentIndex] ?? null) : null)
    }

    // When AudioContext resumes from suspended, re-trigger play on the active
    // channel. Without this, audio.play() called while suspended results in
    // the element "playing" (time advances, seeking works) but no sound or
    // analyser data because the Web Audio graph wasn't processing.
    ctx.addEventListener('statechange', () => {
      if (ctx.state === 'running') {
        const active = activeChannelRef.current === 'A' ? channelARef.current : channelBRef.current
        if (active && !active.audio.paused && stateRef.current.isPlaying) {
          safePlay(active.audio, 'context-resume')
        }
      }
    })

    return ctx
  }, [applyReplayGain])

  const getActiveChannel = useCallback((): AudioChannel | null => {
    return activeChannelRef.current === 'A' ? channelARef.current : channelBRef.current
  }, [])

  const getAnalyserNode = useCallback((): AnalyserNode | null => {
    const channel = activeChannelRef.current === 'A' ? channelARef.current : channelBRef.current
    return channel?.analyserNode ?? null
  }, [])

  /** Stop any active crossfade. Refs only, so the identity stays stable. */
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  const cancelCrossfade = useCallback(() => {
    if (crossfadeTimerRef.current) {
      clearTimeout(crossfadeTimerRef.current)
      crossfadeTimerRef.current = null
    }
    crossfadeActiveRef.current = false

    // Cancel any in-flight Web Audio gain ramps
    const ctx = audioContextRef.current
    if (ctx) {
      const now = ctx.currentTime
      for (const ch of [channelARef.current, channelBRef.current]) {
        if (ch?.gainNode) {
          ch.gainNode.gain.cancelScheduledValues(now)
          ch.gainNode.gain.setValueAtTime(ch.gainNode.gain.value, now)
        }
      }
    }

    // Restore active channel's gain (may have been mid-ramp towards 0)
    const active = activeChannelRef.current === 'A' ? channelARef.current : channelBRef.current
    if (active) {
      const s = stateRef.current
      const track = s.currentIndex >= 0 ? s.queue[s.currentIndex] : null
      applyReplayGain(active, track ?? null)
    }

    const inactive = activeChannelRef.current === 'A' ? channelBRef.current : channelARef.current
    if (inactive) {
      inactive.audio.pause()
      if (inactive.gainNode) inactive.gainNode.gain.value = 0
      // Defer resource release so it doesn't disrupt the active channel's audio graph
      const el = inactive.audio
      setTimeout(() => {
        el.removeAttribute('src')
        el.removeAttribute('data-preload')
        el.load()
      }, 100)
    }
  }, [applyReplayGain])

  // Initialize two audio elements. This must run exactly once: it constructs the
  // two Audio elements and closes the AudioContext on teardown, so any re-run
  // tears down live playback. Everything it reads from player state goes through
  // `stateRef.current` precisely so the listeners see current values without the
  // effect depending on them.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    const audioA = new Audio()
    const audioB = new Audio()
    audioA.volume = stateRef.current.volume
    audioB.volume = stateRef.current.volume
    audioA.preload = 'auto'
    audioB.preload = 'auto'
    audioA.crossOrigin = 'use-credentials'
    audioB.crossOrigin = 'use-credentials'

    channelARef.current = { audio: audioA, source: null, gainNode: null, analyserNode: null }
    channelBRef.current = { audio: audioB, source: null, gainNode: null, analyserNode: null }

    // Only process events from the currently active channel
    const isActive = (e: Event) => e.target === (activeChannelRef.current === 'A' ? audioA : audioB)

    const onTimeUpdate = (e: Event) => {
      if (!isActive(e)) return
      const time = (e.target as HTMLAudioElement).currentTime
      currentTimeRef.current = time

      // Scrobble check (inline to avoid React re-renders)
      if (!scrobbledRef.current) {
        const s = stateRef.current
        if (s.currentIndex >= 0 && s.duration > 0) {
          const track = s.queue[s.currentIndex]
          if (track) {
            if (passedScrobbleMark({ currentTime: time, duration: s.duration })) {
              scrobbledRef.current = true
              // Listening past the scrobble mark in radio is a positive taste signal.
              if (s.radioMode) useAppStore.getState().recordRadioLike(track.id)
              fetch('/api/v1/scrobbles', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ trackId: track.id }),
              }).catch(() => {})
            }
          }
        }
      }

      notifyTimeSubscribers()
    }

    const onDurationChange = (e: Event) => {
      if (!isActive(e)) return
      const dur = (e.target as HTMLAudioElement).duration || 0
      setState((s) => (s.duration === dur ? s : { ...s, duration: dur }))
    }

    const onPlay = (e: Event) => {
      if (!isActive(e)) return
      // A fresh play supersedes any pending deliberate-pause intent.
      intentionalPauseRef.current = false
      if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'playing'
      setState((s) => (s.isPlaying ? s : { ...s, isPlaying: true }))
    }

    const onPause = (e: Event) => {
      if (crossfadeActiveRef.current) return
      if (!isActive(e)) return
      const audio = e.target as HTMLAudioElement

      // A `pause` event on an element that is no longer paused was queued by the
      // media load algorithm (any `src` reassignment fires one) or superseded by
      // a `play()` that landed first. There is nothing to resume, and counting it
      // would spend the auto-resume budget on a non-event.
      if (!audio.paused) return

      const intentional = intentionalPauseRef.current
      intentionalPauseRef.current = false

      // Reassigning src during stall recovery re-runs the media load algorithm,
      // which fires a pause. Playback is already being re-issued, so leave the
      // state alone rather than flashing the UI as stopped.
      if (recovering && !intentional) return

      // A pause we didn't request, on a track that hasn't ended, means the browser
      // paused us (device/route change, context suspension, sleep). Playback is
      // otherwise healthy, so resume rather than showing a dead stop — unless it
      // keeps re-pausing, in which case give up.
      if (!intentional && !audio.ended && stateRef.current.isPlaying) {
        const nowMs = Date.now()
        const plan = planAutoResume({
          count: autoResumeCount,
          lastAt: lastAutoResumeAt,
          now: nowMs,
          recoveryAttempts,
        })
        autoResumeCount = plan.count
        lastAutoResumeAt = nowMs

        if (plan.action === 'resume') {
          logAudioState('Unexpected pause — auto-resuming', audio)
          ensureAudioContext()
          safePlay(audio, 'auto-resume')
          return
        }
        if (plan.action === 'reload') {
          logAudioState('Unexpected pause — reloading stream after repeated auto-resume', audio)
          reloadStream(audio, audio.currentTime)
          return
        }
        logAudioState('Unexpected pause — giving up after repeated auto-resume', audio)
      }

      if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'paused'
      setState((s) => (s.isPlaying ? { ...s, isPlaying: false } : s))
    }

    const onEnded = (e: Event) => {
      if (!isActive(e)) return
      if (crossfadeActiveRef.current) return

      const s = stateRef.current

      if (s.repeat === 'one') {
        const active = activeChannelRef.current === 'A' ? audioA : audioB
        active.currentTime = 0
        safePlay(active, 'repeat-one')
        return
      }

      const outcome = advanceQueue(s)
      if (outcome.type === 'stop') {
        setState((prev) => ({ ...prev, isPlaying: false }))
        return
      }
      if (outcome.type === 'pending-radio') {
        pendingRadioAdvanceRef.current = true
        setState((prev) => ({ ...prev, queue: prev.queue.slice(0, prev.currentIndex + 1) }))
        return
      }

      const nextIndex = outcome.index
      const nextTrack = s.queue[nextIndex]
      const currentTrack = s.queue[s.currentIndex]

      // Gapless swap: if inactive channel already has the next track buffered, flip
      // channels immediately rather than going through the React state → effect path.
      if (nextTrack) {
        const wasA = activeChannelRef.current === 'A'
        const inactiveAudio = wasA ? audioB : audioA
        const inactiveChannel = wasA ? channelBRef.current : channelARef.current
        const nextSrc = nextTrack.streamOverride ?? streamUrl(nextTrack.id)

        if (
          canSwapGaplessly({
            nextSrc,
            nextIsInterstitial: nextTrack.isInterstitial === true,
            currentIsInterstitial: currentTrack?.isInterstitial === true,
            inactive: {
              preloadedSrc: inactiveAudio.getAttribute('data-preload'),
              readyState: inactiveAudio.readyState,
            },
          })
        ) {
          activeChannelRef.current = wasA ? 'B' : 'A'
          inactiveAudio.currentTime = 0
          if (inactiveChannel) applyReplayGain(inactiveChannel, nextTrack)
          safePlay(inactiveAudio, 'gapless-swap')

          const oldAudio = wasA ? audioA : audioB
          oldAudio.pause()
          setTimeout(() => {
            oldAudio.removeAttribute('src')
            oldAudio.removeAttribute('data-preload')
            oldAudio.load()
          }, 100)

          currentTrackIdRef.current = nextTrack.id
          scrobbledRef.current = false
          updateMediaSession(nextTrack)
          setState((prev) => ({
            ...prev,
            currentIndex: nextIndex,
            duration: inactiveAudio.duration || 0,
          }))
          return
        }
      }

      setState((prev) => ({ ...prev, currentIndex: nextIndex }))
    }

    const onError = (e: Event) => {
      if (!isActive(e)) return
      const audio = e.target as HTMLAudioElement
      const err = audio.error
      console.error('[Player] Audio error:', err?.code, err?.message, 'src:', audio.src)

      // An error arriving mid-recovery is most likely the reload itself failing on
      // a stream that was playing fine moments ago, so retry rather than killing
      // playback — but only up to MAX_RECOVERY_ATTEMPTS, so a genuinely dead track
      // still falls through to the stop/advance handling below.
      if (recovering) {
        endRecovery()
        if (recoveryAttempts < MAX_RECOVERY_ATTEMPTS) {
          reloadStream(audio, currentTimeRef.current)
          return
        }
      }

      const s = stateRef.current
      const currentTrack = s.currentIndex >= 0 ? s.queue[s.currentIndex] : null

      // Playback stops here (or silently skips an interstitial), so the reason
      // has to reach the user — a dead player bar looks identical to a bug.
      notifications.show({
        id: `play-error-${currentTrack?.id ?? 'unknown'}`,
        title: currentTrack ? `Can't play "${currentTrack.title}"` : "Can't play this track",
        message: describeMediaError(err),
        color: 'red',
        autoClose: 8000,
      })

      if (currentTrack?.isInterstitial) {
        const outcome = advanceQueue(s)
        if (outcome.type === 'advance') {
          setState((prev) => ({ ...prev, currentIndex: outcome.index }))
          return
        }
      }
      setState((s) => (s.isPlaying ? { ...s, isPlaying: false } : s))
    }

    // Stall recovery: detect when audio stops advancing and reload the stream.
    // The decision logic lives in ~/components/player/audio-recovery; this only
    // samples the element and acts on the verdict.
    let stallWatchdog: ReturnType<typeof setInterval> | null = null
    let baseline: StallBaseline = freshBaseline()
    let recovering = false
    let recoveryTimeout: ReturnType<typeof setTimeout> | null = null
    let recoveryAttempts = 0

    // Auto-resume of unexpected browser-initiated pauses (see AUTO_RESUME_*).
    let autoResumeCount = 0
    let lastAutoResumeAt = 0

    const logAudioState = (label: string, audio: HTMLAudioElement) => {
      const buffered = audio.buffered
      const ranges: string[] = []
      for (let i = 0; i < buffered.length; i++) {
        ranges.push(`${buffered.start(i).toFixed(1)}-${buffered.end(i).toFixed(1)}`)
      }
      console.warn(
        `[Player] ${label}:`,
        `time=${audio.currentTime.toFixed(1)}`,
        `readyState=${audio.readyState}`,
        `networkState=${audio.networkState}`,
        `paused=${audio.paused}`,
        `buffered=[${ranges.join(', ')}]`
      )
    }

    // One hole reload per position, so a stream that truncates at the same offset
    // every time can't spin. The guard releases itself once playback moves on.
    let lastHoleReloadAt = -1

    const reloadAtBufferHole = (audio: HTMLAudioElement, position: number) => {
      if (Math.abs(position - lastHoleReloadAt) < 1) return
      lastHoleReloadAt = position
      logAudioState('Buffer hole ahead — reloading stream', audio)
      reloadStream(audio, position)
    }

    const endRecovery = () => {
      if (recoveryTimeout) {
        clearTimeout(recoveryTimeout)
        recoveryTimeout = null
      }
      recovering = false
    }

    /** Force a fresh HTTP connection for a stream that stopped delivering data. */
    const reloadStream = (audio: HTMLAudioElement, position: number) => {
      const src = audio.currentSrc || audio.src
      if (!src) return

      recoveryAttempts++
      recovering = true
      if (recoveryTimeout) clearTimeout(recoveryTimeout)
      recoveryTimeout = setTimeout(endRecovery, RECOVERY_TIMEOUT_MS)

      // Re-assigning src re-runs the media load algorithm, which opens a new
      // connection. Assigning '' first would resolve against the document base
      // URL and load the app's own HTML as audio, firing a decode error that
      // stops playback outright.
      audio.src = src
      audio.currentTime = position
      safePlay(audio, 'stall-recovery')
      baseline = freshBaseline()
    }

    const giveUpOnStall = (audio: HTMLAudioElement) => {
      logAudioState(`Stall persists after ${recoveryAttempts} reloads — giving up`, audio)
      endRecovery()
      recoveryAttempts = 0
      intentionalPauseRef.current = true
      audio.pause()

      const s = stateRef.current
      const currentTrack = s.currentIndex >= 0 ? s.queue[s.currentIndex] : null
      notifications.show({
        id: `stall-${currentTrack?.id ?? 'unknown'}`,
        title: 'Lost connection',
        message: 'The stream stopped delivering audio. Press play to try again.',
        color: 'red',
        autoClose: 8000,
      })
    }

    const startStallWatchdog = () => {
      if (stallWatchdog) return
      baseline = freshBaseline()
      stallWatchdog = setInterval(() => {
        const channel = activeChannelRef.current
        const active = channel === 'A' ? audioA : audioB
        const now = active.currentTime
        const priorTicks = baseline.ticks

        const { baseline: next, verdict } = observeStall(baseline, {
          channel,
          src: active.currentSrc || active.src,
          time: now,
          paused: active.paused,
          recovering,
          holeAhead: hasBufferHoleAhead(active),
          reloads: recoveryAttempts,
        })
        baseline = next

        const seconds = (ticks: number) => (ticks * STALL_TICK_MS) / 1000

        // Only real progress refills the reload budget. `playing` can't: it fires
        // as soon as a reload calls play(), before any data has arrived.
        if ((verdict === 'healthy' || verdict === 'recovered') && !active.paused) {
          recoveryAttempts = 0
        }

        if (verdict === 'hole-ahead') {
          reloadAtBufferHole(active, now)
        } else if (verdict === 'stalling') {
          logAudioState(`Stall detected (${seconds(baseline.ticks)}s)`, active)
        } else if (verdict === 'exhausted') {
          giveUpOnStall(active)
        } else if (verdict === 'stalled') {
          console.warn(
            `[Player] Stall persists (${seconds(baseline.ticks)}s), reloading stream at`,
            now
          )
          reloadStream(active, now)
        } else if (verdict === 'recovered') {
          console.info('[Player] Recovered from stall after', seconds(priorTicks), 'seconds')
        }
      }, STALL_TICK_MS)
    }

    const stopStallWatchdog = () => {
      if (stallWatchdog) {
        clearInterval(stallWatchdog)
        stallWatchdog = null
      }
    }

    const onWaiting = (e: Event) => {
      if (!isActive(e) || recovering) return
      const audio = e.target as HTMLAudioElement
      // readyState 0 (HAVE_NOTHING) is normal during fresh track loads — don't log
      if (audio.readyState === 0) return
      // Starving at the edge of a hole is terminal; don't wait out the watchdog.
      if (hasBufferHoleAhead(audio)) {
        reloadAtBufferHole(audio, audio.currentTime)
        return
      }
      logAudioState('Waiting for data', audio)
    }

    const onStall = (e: Event) => {
      if (!isActive(e) || recovering) return
      const audio = e.target as HTMLAudioElement
      if (audio.readyState === 0) return
      logAudioState('Network stall', audio)
    }

    const onPlaying = (e: Event) => {
      if (!isActive(e)) return
      autoResumeCount = 0
      endRecovery()
    }

    // Start/stop watchdog with playback, but don't reset ticks during recovery
    const onPlayForWatchdog = (e: Event) => {
      if (!isActive(e)) return
      startStallWatchdog()
    }
    const onPauseForWatchdog = (e: Event) => {
      if (!isActive(e) || recovering) return
      if (!(e.target as HTMLAudioElement).paused) return
      stopStallWatchdog()
    }

    for (const audio of [audioA, audioB]) {
      audio.addEventListener('timeupdate', onTimeUpdate)
      audio.addEventListener('durationchange', onDurationChange)
      audio.addEventListener('ended', onEnded)
      audio.addEventListener('play', onPlay)
      audio.addEventListener('pause', onPause)
      audio.addEventListener('error', onError)
      audio.addEventListener('waiting', onWaiting)
      audio.addEventListener('stalled', onStall)
      audio.addEventListener('playing', onPlaying)
      audio.addEventListener('play', onPlayForWatchdog)
      audio.addEventListener('pause', onPauseForWatchdog)
    }

    return () => {
      stopStallWatchdog()
      endRecovery()
      for (const audio of [audioA, audioB]) {
        audio.removeEventListener('timeupdate', onTimeUpdate)
        audio.removeEventListener('durationchange', onDurationChange)
        audio.removeEventListener('ended', onEnded)
        audio.removeEventListener('play', onPlay)
        audio.removeEventListener('pause', onPause)
        audio.removeEventListener('error', onError)
        audio.removeEventListener('waiting', onWaiting)
        audio.removeEventListener('stalled', onStall)
        audio.removeEventListener('playing', onPlaying)
        audio.removeEventListener('play', onPlayForWatchdog)
        audio.removeEventListener('pause', onPauseForWatchdog)
        audio.pause()
      }
      audioContextRef.current?.close().catch(() => {})
    }
  }, [])

  // Volume sync (both channels)
  useEffect(() => {
    const vol = muted ? 0 : volume
    if (channelARef.current) channelARef.current.audio.volume = vol
    if (channelBRef.current) channelBRef.current.audio.volume = vol
  }, [volume, muted])

  // Re-apply ReplayGain when the toggle or preamp changes. Reads the live queue
  // position off stateRef at the moment the toggle changes; depending on
  // queue/currentIndex would re-run the gain ramp on every track change instead.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    const channel = getActiveChannel()
    if (!channel) return
    const s = stateRef.current
    const track =
      s.currentIndex >= 0 && s.currentIndex < s.queue.length ? s.queue[s.currentIndex] : null
    applyReplayGain(channel, track)
  }, [replayGainEnabled, replayGainPreampDb, getActiveChannel, applyReplayGain])

  return {
    channelARef,
    channelBRef,
    activeChannelRef,
    audioContextRef,
    crossfadeActiveRef,
    crossfadeTimerRef,
    ensureAudioContext,
    getActiveChannel,
    getAnalyserNode,
    applyReplayGain,
    cancelCrossfade,
  }
}
